import { useNavigate } from '@tanstack/react-router'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useConversation } from '@entities/conversation/model'
import { api, unwrap } from '@shared/api/client'
import type { ChannelTopic } from '@shared/api/types'
import { useToast } from '@shared/ui'

import { useAppStore } from '../../model/app-store'
import { ChatSearch } from './ChatSearch'
import styles from './topics.module.css'

export function TopicListScreen({
  conversationId,
  embedded = false,
}: {
  conversationId: string
  embedded?: boolean
}) {
  const navigate = useNavigate()
  const toast = useToast()
  const conversation = useConversation(conversationId)
  const myUserId = useAppStore((state) => state.me?.user.id ?? '')
  const channel = conversation?.channel
  const channelId = channel?.id
  const channelVersion = channel?.version
  const conversationVersion = conversation?.version
  const [topics, setTopics] = useState<ChannelTopic[]>([])
  const [title, setTitle] = useState('')
  const [busy, setBusy] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchOpen, setSearchOpen] = useState(false)
  const canManage = channel?.my_role === 'owner' || channel?.my_role === 'admin'
  const reloadEpoch = useRef(0)

  const reload = useCallback(async () => {
    if (!channelId) return
    const epoch = ++reloadEpoch.current
    setLoading(true)
    try {
      const result = unwrap(
        await api.GET('/channels/{channelId}/topics', { params: { path: { channelId } } }),
      )
      if (epoch !== reloadEpoch.current) return
      setTopics(result.items)
      setError(null)
    } catch {
      if (epoch === reloadEpoch.current) setError('Не удалось загрузить темы. Повторите попытку.')
    } finally {
      if (epoch === reloadEpoch.current) setLoading(false)
    }
  }, [channelId])

  useEffect(() => {
    void reload()
    return () => {
      reloadEpoch.current += 1
    }
  }, [reload, channelVersion, conversationVersion])

  const enable = async () => {
    if (!channel) return
    setBusy(true)
    try {
      unwrap(
        await api.POST('/channels/{channelId}/topics/enable', {
          params: { path: { channelId: channel.id } },
        }),
      )
      await reload()
    } catch {
      toast.show('Не удалось включить темы.')
    } finally {
      setBusy(false)
    }
  }

  const create = async () => {
    if (!channel || !title.trim()) return
    setBusy(true)
    try {
      const result = unwrap(
        await api.POST('/channels/{channelId}/topics', {
          params: { path: { channelId: channel.id } },
          body: { title: title.trim() },
        }),
      )
      setTitle('')
      await reload()
      void navigate({
        to: '/chats/$conversationId/topics/$topicId',
        params: { conversationId, topicId: result.id },
      })
    } catch {
      toast.show('Не удалось создать тему.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <section className={styles['screen']} aria-label="Темы группы">
      <header className={styles['header']}>
        {!embedded ? (
          <button type="button" onClick={() => void navigate({ to: '/chats' })} aria-label="Назад к чатам">
            ‹
          </button>
        ) : null}
        <div>
          <h1>{channel?.title ?? 'Группа'}</h1>
          <p>Темы группы</p>
        </div>
        <button type="button" aria-label="Поиск по группе" onClick={() => setSearchOpen(true)}>
          ⌕
        </button>
      </header>
      {searchOpen ? (
        <ChatSearch
          conversationId={conversationId}
          myUserId={myUserId}
          onClose={() => setSearchOpen(false)}
          onPick={(seq, topicId) => {
            if (topicId)
              void navigate({
                to: '/chats/$conversationId/topics/$topicId',
                params: { conversationId, topicId },
                search: { seq },
              })
          }}
        />
      ) : null}
      {!searchOpen ? (
        <>
          {error ? (
            <div className={styles['notice']} role="alert">
              {error}{' '}
              <button type="button" onClick={() => void reload()}>
                Повторить
              </button>
            </div>
          ) : null}
          {loading && topics.length === 0 ? <p className={styles['notice']}>Загрузка тем…</p> : null}
          {!loading && topics.length === 0 && canManage ? (
            <div className={styles['notice']}>
              <p>Обсуждения по темам помогут разделить разговор.</p>
              <button type="button" disabled={busy} onClick={() => void enable()}>
                Включить темы
              </button>
            </div>
          ) : null}
          <div className={styles['list']}>
            {topics.map((topic) => (
              <button
                key={topic.id}
                type="button"
                className={styles['topic']}
                onClick={() =>
                  void navigate({
                    to: '/chats/$conversationId/topics/$topicId',
                    params: { conversationId, topicId: topic.id },
                  })
                }
              >
                <span className={styles['glyph']} aria-hidden="true">
                  {topic.general ? '💬' : '#'}
                </span>
                <span className={styles['topicText']}>
                  <strong>{topic.title}</strong>
                  <small>
                    {topic.state === 'closed'
                      ? 'Тема закрыта'
                      : topic.last_message?.text || 'Пока нет сообщений'}
                  </small>
                </span>
                {topic.mentions_count > 0 ? (
                  <span className={styles['badge']}>@{topic.mentions_count}</span>
                ) : topic.unread_count > 0 ? (
                  <span className={styles['badge']}>{topic.unread_count}</span>
                ) : null}
              </button>
            ))}
          </div>
          {canManage && topics.length > 0 ? (
            <form
              className={styles['create']}
              onSubmit={(event) => {
                event.preventDefault()
                void create()
              }}
            >
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="Название новой темы"
                maxLength={80}
                aria-label="Название новой темы"
              />
              <button type="submit" disabled={busy || !title.trim()}>
                Создать
              </button>
            </form>
          ) : null}
        </>
      ) : null}
    </section>
  )
}
